/**
 * Simple Image Editor Component for Recipe Photos
 * Provides basic rotation functionality - cropping removed due to library compatibility issues
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  ButtonGroup
} from '@mui/material';
import {
  Close,
  RotateLeft,
  RotateRight,
  DocumentScanner,
  Check
} from '@mui/icons-material';

function ImageEditor({ open, onClose, onSave, imageFile }) {
  const [rotation, setRotation] = useState(0);
  const [imageSrc, setImageSrc] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load image when component mounts or imageFile changes
  React.useEffect(() => {
    if (imageFile) {
      console.log('📂 Loading image file:', imageFile.name, imageFile.size, 'bytes');
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('✅ Image loaded successfully!');
        setImageSrc(reader.result);
        setRotation(0); // Reset rotation
      };
      reader.onerror = (error) => {
        console.error('❌ Error loading image:', error);
      };
      reader.readAsDataURL(imageFile);
    } else {
      console.log('⚠️ No image file provided');
      setImageSrc(null);
    }
  }, [imageFile]);

  const getRotatedImage = async () => {
    if (!imageSrc) {
      console.log('Missing imageSrc');
      return null;
    }

    // If no rotation, just return the original file
    if (rotation === 0) {
      console.log('No rotation needed, returning original file');
      return imageFile;
    }

    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = imageSrc;
      
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });

      console.log('Rotating image by', rotation, 'degrees');
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate new dimensions for rotation
      const radians = (rotation * Math.PI) / 180;
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      canvas.width = image.width * cos + image.height * sin;
      canvas.height = image.width * sin + image.height * cos;

      // Center the rotation
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('Failed to create blob from rotated image');
            resolve(null);
            return;
          }
          const file = new File([blob], imageFile.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          console.log('✅ Rotated image created successfully');
          resolve(file);
        }, 'image/jpeg', 0.95);
      });
    } catch (error) {
      console.error('Error rotating image:', error);
      return null;
    }
  };

  const handleSave = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const processedFile = await getRotatedImage();
      if (processedFile) {
        onSave(processedFile);
      } else {
        alert('Failed to process image. Please try again.');
      }
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Error saving image: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const getTransformStyle = () => {
    return {
      transform: `rotate(${rotation}deg)`,
      transition: 'transform 0.3s ease',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DocumentScanner color="primary" />
          Edit Recipe Photo
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            minHeight: 400
          }}
        >
          {/* Image Preview */}
          <Box
            sx={{
              width: '100%',
              height: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Preview"
                style={getTransformStyle()}
              />
            ) : (
              <Typography variant="h6" color="text.secondary">
                No image loaded
              </Typography>
            )}
          </Box>

          {/* Rotation Controls */}
          {imageSrc && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Rotate Image
              </Typography>
              <ButtonGroup variant="outlined" size="large">
                <Button
                  startIcon={<RotateLeft />}
                  onClick={handleRotateLeft}
                >
                  Rotate Left
                </Button>
                <Button
                  endIcon={<RotateRight />}
                  onClick={handleRotateRight}
                >
                  Rotate Right
                </Button>
              </ButtonGroup>
              <Typography variant="body2" color="text.secondary">
                Current rotation: {rotation}°
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} size="large">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<Check />}
          disabled={isProcessing || !imageSrc}
          size="large"
        >
          {isProcessing ? 'Processing...' : 'Save Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImageEditor;
